/**
 * © 2025 Sixsmith Games. All rights reserved.
 * This software and associated documentation files are proprietary and confidential.
 */

import axios, { AxiosHeaders } from 'axios';
import { Project, ContentBlock, APIResponse, PaginatedResponse } from '../types';
import type { WritingCanonProjectReport } from '../../../src/shared/canon/writingCanon';

// Use relative URL - works in both development (via Vite proxy) and production (same-origin)
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

type AuthOptions = {
  token?: string | null;
};

let apiAuthToken: string | null = null;
let apiAuthTokenProvider: (() => Promise<string | null>) | null = null;

export function setApiAuthToken(token: string | null) {
  apiAuthToken = token;
}

export function setApiAuthTokenProvider(provider: (() => Promise<string | null>) | null) {
  apiAuthTokenProvider = provider;
}

export function authHeaders(token?: string | null) {
  const resolvedToken = token === undefined ? apiAuthToken : token;
  return resolvedToken
    ? { Authorization: `Bearer ${resolvedToken}` }
    : {};
}

async function getApiFetchToken() {
  if (!apiAuthTokenProvider) return apiAuthToken;
  const token = await apiAuthTokenProvider();
  apiAuthToken = token;
  return token;
}

export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  if (!headers.has('Authorization')) {
    const token = await getApiFetchToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }
  return fetch(input, { ...init, headers });
}

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    if (apiAuthToken) {
      config.headers = AxiosHeaders.from(config.headers);
      if (!config.headers.has('Authorization')) {
        config.headers.set('Authorization', `Bearer ${apiAuthToken}`);
      }
    }
    console.log('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.status, error.config?.url, error.message);
    return Promise.reject(error);
  }
);

export const projectApi = {
  getAll: async ({ token, page = 1, limit = 20 }: AuthOptions & { page?: number; limit?: number } = {}): Promise<PaginatedResponse<Project>> => {
    const response = await api.get(`/projects?page=${page}&limit=${limit}`, {
      headers: authHeaders(token),
    });
    return response.data;
  },

  getById: async (id: string): Promise<APIResponse<Project>> => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },

  create: async (data: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>, { token }: AuthOptions = {}): Promise<APIResponse<Project>> => {
    const response = await api.post('/projects', data, {
      headers: authHeaders(token),
    });
    return response.data;
  },

  update: async (id: string, data: Partial<Omit<Project, 'id' | 'createdAt' | 'updatedAt'>>, { token }: AuthOptions = {}): Promise<APIResponse<Project>> => {
    const response = await api.put(`/projects/${id}`, data, {
      headers: authHeaders(token),
    });
    return response.data;
  },

  delete: async (id: string, { token }: AuthOptions = {}): Promise<APIResponse> => {
    const response = await api.delete(`/projects/${id}`, {
      headers: authHeaders(token),
    });
    return response.data;
  },
};

export const contentApi = {
  getByProjectId: async (projectId: string, page = 1, limit = 50): Promise<PaginatedResponse<ContentBlock>> => {
    const response = await api.get(`/content/project/${projectId}?page=${page}&limit=${limit}`);
    return response.data;
  },

  scanProjectCanonCheck: async (
    projectId: string,
    blockIds?: string[],
  ): Promise<APIResponse<WritingCanonProjectReport>> => {
    const response = await api.post(`/content/project/${projectId}/canon-check`, {
      block_ids: Array.isArray(blockIds) ? blockIds : [],
    });
    return response.data;
  },

  getById: async (id: string): Promise<APIResponse<ContentBlock>> => {
    const response = await api.get(`/content/${id}`);
    return response.data;
  },

  getChildren: async (id: string): Promise<APIResponse<ContentBlock[]>> => {
    const response = await api.get(`/content/${id}/children`);
    return response.data;
  },

  create: async (data: Omit<ContentBlock, 'id' | 'createdAt' | 'updatedAt'>): Promise<APIResponse<ContentBlock>> => {
    const response = await api.post('/content', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Omit<ContentBlock, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>>): Promise<APIResponse<ContentBlock>> => {
    const response = await api.put(`/content/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<APIResponse> => {
    const response = await api.delete(`/content/${id}`);
    return response.data;
  },

  reorder: async (projectId: string, blockIds: string[]): Promise<APIResponse> => {
    const response = await api.post(`/content/reorder/${projectId}`, { blockIds });
    return response.data;
  },
};

export const healthApi = {
  check: async (): Promise<APIResponse> => {
    const response = await api.get('/health');
    return response.data;
  },
};
